import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Target, Plus, Edit, Trash2, Eye, Save, X, BarChart3 } from "lucide-react";
import { API } from "./shared/utils";
import axios from "axios";

const AdminAssessmentManager = () => {
  const [assessments, setAssessments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showQuestions, setShowQuestions] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", category: "general", active: true,
    reflection_enabled: true, scale_min: 1, scale_max: 5
  });

  useEffect(() => { fetchAssessments(); }, []);

  const fetchAssessments = async () => {
    try {
      const res = await axios.get(`${API}/assessments/all`);
      setAssessments(res.data);
    } catch (err) { console.error(err); }
  };

  const saveAssessment = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/assessments/${editing.id}`, form);
      } else {
        await axios.post(`${API}/assessments`, form);
      }
      fetchAssessments();
      setShowForm(false);
      setEditing(null);
      setForm({ title: "", description: "", category: "general", active: true, reflection_enabled: true, scale_min: 1, scale_max: 5 });
    } catch (err) { alert("Error saving: " + (err.response?.data?.detail || err.message)); }
  };

  const loadQuestions = async (assessment) => {
    try {
      const res = await axios.get(`${API}/assessments/${assessment.id}`);
      setQuestions(res.data.questions || []);
      setShowQuestions(assessment);
    } catch (err) { console.error(err); }
  };

  const addQuestion = async () => {
    if (!newQuestion.trim()) return;
    try {
      await axios.post(`${API}/assessments/${showQuestions.id}/questions`, { question_text: newQuestion.trim() });
      setNewQuestion("");
      loadQuestions(showQuestions);
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  const updateQuestion = async (qId, text) => {
    try {
      await axios.put(`${API}/assessments/questions/${qId}`, { question_text: text });
      setEditingQuestion(null);
      loadQuestions(showQuestions);
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  const deleteQuestion = async (qId) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await axios.delete(`${API}/assessments/questions/${qId}`);
      loadQuestions(showQuestions);
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  const viewStats = async (assessment) => {
    try {
      const res = await axios.get(`${API}/assessments/${assessment.id}/stats`);
      setStats(res.data);
      setShowStats(assessment);
    } catch (err) { console.error(err); }
  };

  const toggleActive = async (assessment) => {
    try {
      await axios.put(`${API}/assessments/${assessment.id}`, { active: !assessment.active });
      fetchAssessments();
    } catch (err) { alert("Error toggling status"); }
  };

  return (
    <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-indigo-400" />
          Self-Assessments
        </CardTitle>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ title: "", description: "", category: "general", active: true, reflection_enabled: true, scale_min: 1, scale_max: 5 }); setShowForm(true); }} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-1" /> New Assessment
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {assessments.length === 0 ? (
          <p className="text-slate-400 text-center py-4">No assessments yet. Create one or run seed-assessment.</p>
        ) : assessments.map((a) => (
          <div key={a.id} className="p-3 rounded-lg bg-slate-900/50 border border-slate-700/50 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-white text-sm font-medium">{a.title}</p>
                <Badge className={a.active ? "bg-green-500/20 text-green-300 text-xs" : "bg-red-500/20 text-red-300 text-xs"}>
                  {a.active ? "Active" : "Inactive"}
                </Badge>
                <Badge className="bg-slate-600/50 text-slate-300 text-xs">{a.category}</Badge>
              </div>
              <p className="text-slate-500 text-xs mt-0.5">{a.description?.substring(0, 80)}</p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" title="View Stats" onClick={() => viewStats(a)} className="h-7 w-7 p-0 border-slate-600 text-slate-300 hover:bg-slate-700">
                <BarChart3 className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" title="Manage Questions" onClick={() => loadQuestions(a)} className="h-7 w-7 p-0 border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/20">
                <Eye className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" title="Edit" onClick={() => { setEditing(a); setForm({ title: a.title, description: a.description || "", category: a.category || "general", active: a.active, reflection_enabled: a.reflection_enabled ?? true, scale_min: a.scale_min || 1, scale_max: a.scale_max || 5 }); setShowForm(true); }} className="h-7 w-7 p-0 border-slate-600 text-slate-300 hover:bg-slate-700">
                <Edit className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => toggleActive(a)} className={`h-7 px-2 text-xs ${a.active ? 'border-red-500/50 text-red-300 hover:bg-red-500/20' : 'border-green-500/50 text-green-300 hover:bg-green-500/20'}`}>
                {a.active ? "Deactivate" : "Activate"}
              </Button>
            </div>
          </div>
        ))}

        {/* Create/Edit Assessment Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">{editing ? "Edit Assessment" : "New Assessment"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-slate-300 text-sm">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="bg-slate-800 border-slate-600 text-white" placeholder="Assessment title" /></div>
              <div><Label className="text-slate-300 text-sm">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-slate-800 border-slate-600 text-white" rows={2} /></div>
              <div><Label className="text-slate-300 text-sm">Category</Label>
                <Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="bg-slate-800 border-slate-600 text-white" placeholder="e.g., communication, leadership" /></div>
              <div className="flex gap-4">
                <div><Label className="text-slate-300 text-sm">Scale Min</Label>
                  <Input type="number" value={form.scale_min} onChange={(e) => setForm({...form, scale_min: parseInt(e.target.value)})} className="bg-slate-800 border-slate-600 text-white w-20" /></div>
                <div><Label className="text-slate-300 text-sm">Scale Max</Label>
                  <Input type="number" value={form.scale_max} onChange={(e) => setForm({...form, scale_max: parseInt(e.target.value)})} className="bg-slate-800 border-slate-600 text-white w-20" /></div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} className="rounded" /> Active
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={form.reflection_enabled} onChange={(e) => setForm({...form, reflection_enabled: e.target.checked})} className="rounded" /> Enable Reflections
                </label>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-600 text-slate-300">Cancel</Button>
                <Button onClick={saveAssessment} className="bg-indigo-600 hover:bg-indigo-700">
                  <Save className="w-4 h-4 mr-1" /> Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Questions Dialog */}
        <Dialog open={!!showQuestions} onOpenChange={() => setShowQuestions(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Questions: {showQuestions?.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div key={q.id} className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 flex items-start gap-2">
                  <span className="text-indigo-400 font-bold text-sm mt-1">{idx + 1}.</span>
                  {editingQuestion === q.id ? (
                    <div className="flex-1 flex gap-1">
                      <Input defaultValue={q.question_text} id={`edit-q-${q.id}`} className="bg-slate-900 border-slate-600 text-white text-sm flex-1" />
                      <Button size="sm" onClick={() => updateQuestion(q.id, document.getElementById(`edit-q-${q.id}`).value)} className="bg-green-600 h-8 w-8 p-0"><Save className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingQuestion(null)} className="h-8 w-8 p-0 border-slate-600"><X className="w-3 h-3" /></Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-white text-sm flex-1">{q.question_text}</p>
                      <Button size="sm" variant="outline" onClick={() => setEditingQuestion(q.id)} className="h-7 w-7 p-0 border-slate-600 text-slate-300 hover:bg-slate-700"><Edit className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => deleteQuestion(q.id)} className="h-7 w-7 p-0 border-red-500/50 text-red-300 hover:bg-red-500/20"><Trash2 className="w-3 h-3" /></Button>
                    </>
                  )}
                </div>
              ))}
              <div className="flex gap-2 pt-2 border-t border-slate-700/50">
                <Input value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} placeholder="Add a new question..." className="bg-slate-800 border-slate-600 text-white text-sm flex-1" onKeyDown={(e) => e.key === 'Enter' && addQuestion()} />
                <Button size="sm" onClick={addQuestion} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stats Dialog */}
        <Dialog open={!!showStats} onOpenChange={() => setShowStats(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-400" />
                {showStats?.title} — Stats
              </DialogTitle>
            </DialogHeader>
            {stats && (
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-indigo-400">{stats.total_attempts}</p>
                  <p className="text-xs text-slate-400">Total Attempts</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-purple-400">{stats.unique_users}</p>
                  <p className="text-xs text-slate-400">Unique Users</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 text-center col-span-2">
                  <p className="text-3xl font-bold text-amber-400">{stats.avg_score}</p>
                  <p className="text-xs text-slate-400">Avg Score (out of {stats.max_score})</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AdminAssessmentManager;
